import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Ruler, ChevronDown, ChevronRight, Send, Pencil, Check, Trash2, CheckCircle, AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700",
  published: "bg-emerald-100 text-emerald-800",
};

const EMPTY_POM_ROW = { pom: "", sizes: {} };
const EMPTY_GRADE_RULE = { pom: "", increment: "" };

export default function SizeChartManagement() {
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editingPom, setEditingPom] = useState(null);
  const [editingGrade, setEditingGrade] = useState(null);
  const [form, setForm] = useState({ name: "", garment_categories: "", base_size: "", fit_block_name: "" });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [fitBlocks, setFitBlocks] = useState([]);
  const [copySource, setCopySource] = useState("");
  const [validation, setValidation] = useState(null);

  const load = () => {
    api.get("/size-charts")
      .then((r) => setCharts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/fit-blocks").then((r) => setFitBlocks(r.data)).catch(() => {});
  }, []);

  const loadDetail = async (id) => {
    try {
      const r = await api.get(`/size-charts/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleExpand = (id) => {
    if (expandedChart === id) {
      setExpandedChart(null);
      setDetail(null);
      setEditingPom(null);
      setEditingGrade(null);
    } else {
      setExpandedChart(id);
      loadDetail(id);
    }
  };

  const create = async () => {
    setSaving(true);
    try {
      const categories = form.garment_categories
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const selectedBlock = fitBlocks.find((b) => b.name === form.fit_block_name);
      await api.post("/size-charts", {
        name: form.name,
        garment_categories: categories,
        base_size: form.base_size,
        fit_block_name: form.fit_block_name,
        fit_block: selectedBlock ? selectedBlock.measurements : {},
        pom_rows: [],
        grade_rules: [],
      });
      toast.success("Size chart created");
      setShowCreate(false);
      setForm({ name: "", garment_categories: "", base_size: "", fit_block_name: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteChart = async (id) => {
    if (!window.confirm("Delete this size chart?")) return;
    try {
      await api.delete(`/size-charts/${id}`);
      toast.success("Size chart deleted");
      if (expandedChart === id) { setExpandedChart(null); setDetail(null); }
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const publish = async (id) => {
    setPublishing(id);
    try {
      await api.post(`/size-charts/${id}/publish`);
      toast.success("Size chart published");
      load();
      if (expandedChart === id) loadDetail(id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPublishing(null);
    }
  };

  /* --- POM row inline editing --- */
  const addPomRow = () => {
    if (!detail) return;
    const updated = [...(detail.pom_rows || []), { ...EMPTY_POM_ROW }];
    savePomRows(updated);
  };

  const updatePomRow = (idx, field, value) => {
    const rows = [...(detail.pom_rows || [])];
    if (field === "pom") {
      rows[idx] = { ...rows[idx], pom: value };
    } else {
      rows[idx] = { ...rows[idx], sizes: { ...rows[idx].sizes, [field]: value } };
    }
    setDetail({ ...detail, pom_rows: rows });
  };

  const savePomRows = async (rows) => {
    const pom_rows = rows || detail.pom_rows;
    try {
      const r = await api.patch(`/size-charts/${detail.id}`, { pom_rows });
      setDetail(r.data);
      setEditingPom(null);
      toast.success("POM rows updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deletePomRow = async (idx) => {
    const rows = (detail.pom_rows || []).filter((_, i) => i !== idx);
    savePomRows(rows);
  };

  /* --- Grade rule inline editing --- */
  const addGradeRule = () => {
    if (!detail) return;
    const updated = [...(detail.grade_rules || []), { ...EMPTY_GRADE_RULE }];
    saveGradeRules(updated);
  };

  const updateGradeRule = (idx, field, value) => {
    const rules = [...(detail.grade_rules || [])];
    rules[idx] = { ...rules[idx], [field]: value };
    setDetail({ ...detail, grade_rules: rules });
  };

  const saveGradeRules = async (rules) => {
    const grade_rules = rules || detail.grade_rules;
    try {
      const r = await api.patch(`/size-charts/${detail.id}`, { grade_rules });
      setDetail(r.data);
      setEditingGrade(null);
      toast.success("Grade rules updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteGradeRule = async (idx) => {
    const rules = (detail.grade_rules || []).filter((_, i) => i !== idx);
    saveGradeRules(rules);
  };

  /* Collect all size keys across pom_rows */
  const sizeKeys = detail
    ? [...new Set((detail.pom_rows || []).flatMap((r) => Object.keys(r.sizes || {})))]
    : [];

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Size Charts</h1>
          <p className="text-zinc-500 mt-2 text-sm">Size charts, POM measurements and grade rules.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New size chart
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : charts.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <Ruler size={26} className="mx-auto mb-3" />
          <p className="text-sm">No size charts yet.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {charts.map((c) => (
            <div key={c.id} className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
              <div className="flex items-center px-6 py-4 cursor-pointer hover:bg-zinc-50" onClick={() => toggleExpand(c.id)}>
                <div className="w-8 h-8 rounded border border-zinc-200 bg-zinc-100 flex items-center justify-center mr-4">
                  <Ruler size={14} className="text-zinc-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  <p className="text-xs text-zinc-400">
                    Base: {c.base_size} | Categories: {(c.garment_categories || []).join(", ") || "None"}
                  </p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium mr-3 ${STATUS_STYLE[c.status] || STATUS_STYLE.draft}`}>
                  {c.status}
                </span>
                {c.status === "draft" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); publish(c.id); }}
                    disabled={publishing === c.id}
                    className="mr-3 inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-md text-xs font-medium hover:bg-emerald-100 disabled:opacity-40"
                  >
                    <Send size={12} /> {publishing === c.id ? "Publishing..." : "Publish"}
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); deleteChart(c.id); }}
                  className="mr-3 p-1.5 hover:bg-red-50 rounded-md"
                  title="Delete size chart"
                >
                  <Trash2 size={14} className="text-red-400" />
                </button>
                {expandedChart === c.id ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
              </div>

              {expandedChart === c.id && detail && detail.id === c.id && (
                <div className="px-6 pb-6 border-t border-zinc-100 pt-4 space-y-6">
                  {/* Meta */}
                  <div className="flex gap-6 text-xs text-zinc-400">
                    <span>Version: {detail.version}</span>
                    <span>Created: {detail.created_at ? new Date(detail.created_at).toLocaleDateString() : "-"}</span>
                    <span>Updated: {detail.updated_at ? new Date(detail.updated_at).toLocaleDateString() : "-"}</span>
                  </div>

                  {/* Fit Block */}
                  {detail.fit_block_name && (
                    <div className="border border-zinc-100 bg-zinc-50 rounded-md p-3">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1.5">Fit Block</p>
                      <p className="text-sm font-medium">{detail.fit_block_name}</p>
                      {detail.fit_block && Object.keys(detail.fit_block).length > 0 && (
                        <div className="mt-2 grid grid-cols-3 gap-x-4 gap-y-1">
                          {Object.entries(detail.fit_block).map(([k, v]) => (
                            <div key={k} className="text-xs">
                              <span className="text-zinc-400">{k.replace(/_/g, " ")}: </span>
                              <span className="font-medium">{v} cm</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Grade Rule Copy */}
                  {detail.status === "draft" && charts.filter((ch) => ch.id !== detail.id && (ch.grade_rules || []).length > 0).length > 0 && (
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-1 block">Copy grade rules from</label>
                        <select value={copySource} onChange={(e) => setCopySource(e.target.value)}
                          className="w-full border border-zinc-300 rounded-md px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-zinc-950">
                          <option value="">Select source chart...</option>
                          {charts.filter((ch) => ch.id !== detail.id && (ch.grade_rules || []).length > 0).map((ch) => (
                            <option key={ch.id} value={ch.id}>{ch.name} ({(ch.grade_rules || []).length} rules)</option>
                          ))}
                        </select>
                      </div>
                      <button onClick={async () => {
                        if (!copySource) return;
                        try {
                          const r = await api.post(`/size-charts/${detail.id}/apply-grade-rules`, { source_chart_id: copySource });
                          setDetail(r.data);
                          setCopySource("");
                          toast.success("Grade rules copied");
                          load();
                        } catch (e) { toast.error(apiError(e)); }
                      }} disabled={!copySource}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs font-medium hover:bg-zinc-800 disabled:opacity-40">
                        Copy
                      </button>
                    </div>
                  )}

                  {/* POM Rows / Sizes table */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs uppercase tracking-[0.15em] text-zinc-400">POM Measurements (Sizes)</h4>
                      <button onClick={addPomRow}
                        className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900">
                        <Plus size={12} /> Add row
                      </button>
                    </div>
                    {(detail.pom_rows || []).length === 0 ? (
                      <p className="text-xs text-zinc-400">No POM rows defined. Add a row to start.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-zinc-200 rounded">
                          <thead>
                            <tr className="bg-zinc-50">
                              <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 border-b border-zinc-200">POM</th>
                              {sizeKeys.map((sk) => (
                                <th key={sk} className="px-3 py-2 text-left text-xs font-medium text-zinc-500 border-b border-zinc-200">{sk}</th>
                              ))}
                              <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500 border-b border-zinc-200 w-20">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.pom_rows || []).map((row, idx) => (
                              <tr key={idx} className="border-b border-zinc-100 last:border-b-0">
                                <td className="px-3 py-2">
                                  {editingPom === idx ? (
                                    <input
                                      value={row.pom}
                                      onChange={(e) => updatePomRow(idx, "pom", e.target.value)}
                                      className="border border-zinc-300 rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-zinc-950"
                                    />
                                  ) : (
                                    <span className="text-xs">{row.pom || "-"}</span>
                                  )}
                                </td>
                                {sizeKeys.map((sk) => (
                                  <td key={sk} className="px-3 py-2">
                                    {editingPom === idx ? (
                                      <input
                                        value={(row.sizes || {})[sk] || ""}
                                        onChange={(e) => updatePomRow(idx, sk, e.target.value)}
                                        className="border border-zinc-300 rounded px-2 py-1 text-xs w-20 focus:outline-none focus:ring-1 focus:ring-zinc-950"
                                      />
                                    ) : (
                                      <span className="text-xs">{(row.sizes || {})[sk] || "-"}</span>
                                    )}
                                  </td>
                                ))}
                                <td className="px-3 py-2 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    {editingPom === idx ? (
                                      <button onClick={() => savePomRows()} className="p-1 hover:bg-emerald-50 rounded" title="Save">
                                        <Check size={13} className="text-emerald-600" />
                                      </button>
                                    ) : (
                                      <button onClick={() => setEditingPom(idx)} className="p-1 hover:bg-zinc-100 rounded" title="Edit">
                                        <Pencil size={13} className="text-zinc-400" />
                                      </button>
                                    )}
                                    <button onClick={() => deletePomRow(idx)} className="p-1 hover:bg-red-50 rounded" title="Delete">
                                      <Trash2 size={13} className="text-red-400" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Grade Rules table */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs uppercase tracking-[0.15em] text-zinc-400">Grade Rules</h4>
                      <div className="flex gap-2">
                        <button onClick={async () => {
                          try {
                            const r = await api.post(`/size-charts/${detail.id}/validate-grade-rules`);
                            setValidation(r.data);
                            if (r.data.valid) toast.success("All grade rules valid");
                            else toast.error(`${r.data.errors.length} validation error(s)`);
                          } catch (e) { toast.error(apiError(e)); }
                        }} className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900">
                          <CheckCircle size={12} /> Validate
                        </button>
                        <button onClick={addGradeRule}
                          className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900">
                          <Plus size={12} /> Add rule
                        </button>
                      </div>
                    </div>
                    {/* Validation results */}
                    {validation && (
                      <div className={`mb-3 p-3 rounded-md text-xs ${validation.valid ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
                        <div className="flex items-center gap-1 font-medium mb-1">
                          {validation.valid ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
                          {validation.message}
                        </div>
                        {(validation.errors || []).map((err, i) => (
                          <p key={i} className="ml-4">{err.message}</p>
                        ))}
                      </div>
                    )}
                    {(detail.grade_rules || []).length === 0 ? (
                      <p className="text-xs text-zinc-400">No grade rules defined. Add a rule to start.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-zinc-200 rounded">
                          <thead>
                            <tr className="bg-zinc-50">
                              <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 border-b border-zinc-200">POM</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 border-b border-zinc-200">Increment</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500 border-b border-zinc-200 w-20">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.grade_rules || []).map((rule, idx) => (
                              <tr key={idx} className="border-b border-zinc-100 last:border-b-0">
                                <td className="px-3 py-2">
                                  {editingGrade === idx ? (
                                    <input
                                      value={rule.pom}
                                      onChange={(e) => updateGradeRule(idx, "pom", e.target.value)}
                                      className="border border-zinc-300 rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-zinc-950"
                                    />
                                  ) : (
                                    <span className="text-xs">{rule.pom || "-"}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {editingGrade === idx ? (
                                    <input
                                      value={rule.increment}
                                      onChange={(e) => updateGradeRule(idx, "increment", e.target.value)}
                                      className="border border-zinc-300 rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-zinc-950"
                                    />
                                  ) : (
                                    <span className="text-xs">{rule.increment || "-"}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    {editingGrade === idx ? (
                                      <button onClick={() => saveGradeRules()} className="p-1 hover:bg-emerald-50 rounded" title="Save">
                                        <Check size={13} className="text-emerald-600" />
                                      </button>
                                    ) : (
                                      <button onClick={() => setEditingGrade(idx)} className="p-1 hover:bg-zinc-100 rounded" title="Edit">
                                        <Pencil size={13} className="text-zinc-400" />
                                      </button>
                                    )}
                                    <button onClick={() => deleteGradeRule(idx)} className="p-1 hover:bg-red-50 rounded" title="Delete">
                                      <Trash2 size={13} className="text-red-400" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New size chart</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Chart name (e.g. Men's Casual Tops)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.base_size} onChange={(e) => setForm({ ...form, base_size: e.target.value })} placeholder="Base size (e.g. M, 38, L)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.garment_categories} onChange={(e) => setForm({ ...form, garment_categories: e.target.value })} placeholder="Categories (comma-separated, e.g. T-Shirt, Polo)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Fit Block (body measurement template)</label>
                <select value={form.fit_block_name} onChange={(e) => setForm({ ...form, fit_block_name: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">No fit block (custom)</option>
                  {fitBlocks.map((b) => <option key={b.name} value={b.name}>{b.name} — {(b.garment_types || []).join(", ")}</option>)}
                </select>
              </div>
              <button onClick={create} disabled={!form.name || !form.base_size || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create size chart"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
